import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Truck, LogOut, MapPin, Phone, Check, Clock, Package, DollarSign, Copy, Navigation, User, FileText } from 'lucide-react';

import { useAuth, API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { getErrorMessage } from '../utils/errorHandler';
import { formatAmount } from '../utils/format';
const DeliveryDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dailySummary, setDailySummary] = useState(null);
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadOrders = async () => {
    try {
      // Solo la cola activa: este panel agrupa por delivery_status (mi cliente ya
      // entregado sale solo) y por eso no necesita el historial de 'delivered', que
      // se consulta por /delivery/daily-report. Sin filtro de estado, la paginacion
      // devolvía las órdenes más antiguas del tenant y las nuevas no aparecían.
      const response = await axios.get(`${API}/orders?status=pending&status=in_preparation&status=ready&limit=200`, {
        headers: getAuthHeaders()
      });
      const deliveryOrders = (Array.isArray(response.data) ? response.data : []).filter(
        (o) => o.order_type === 'delivery' && o.status !== 'cancelled'
      );
      setOrders(deliveryOrders);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar órdenes'));
    }
  };

  const loadDailySummary = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API}/delivery/daily-report?date=${today}`, {
        headers: getAuthHeaders()
      });
      
      // Validate response structure
      if (response.data && typeof response.data === 'object' && Array.isArray(response.data.orders)) {
        setDailySummary(response.data);
        setShowSummary(true);
      } else {
        toast.error('Error: Formato de respuesta inválido');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar resumen diario'));
    }
  };

  const acceptOrder = async (orderId) => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/orders/${orderId}/assign-delivery?delivery_person_id=${user.id}`,
        {},
        { headers: getAuthHeaders() }
      );
      await axios.put(
        `${API}/orders/${orderId}/delivery-status?delivery_status=on_the_way`,
        {},
        { headers: getAuthHeaders() }
      );
      // toast.success('Orden aceptada');
      loadOrders();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al aceptar orden'));
    }
    setLoading(false);
  };

  const completeDelivery = async (orderId) => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/orders/${orderId}/delivery-status?delivery_status=delivered`,
        {},
        { headers: getAuthHeaders() }
      );
      await axios.put(
        `${API}/orders/${orderId}/status?status=delivered`,
        {},
        { headers: getAuthHeaders() }
      );
      // toast.success('Entrega completada');
      loadOrders();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al completar entrega'));
    }
    setLoading(false);
  };

  const copyOrderLink = async (orderId) => {
    const link = `${window.location.origin}/tracking/${orderId}`;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
        toast.success('Enlace de tracking copiado');
      } else {
        // Fallback method for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          textArea.remove();
          toast.success('Enlace de tracking copiado');
        } catch {
          textArea.remove();
          // If both methods fail, show the link
          toast.info(`Enlace: ${link}`, { duration: 5000 });
        }
      }
    } catch {
      // If clipboard API fails, show the link instead
      toast.info(`Enlace: ${link}`, { duration: 5000 });
    }
  };

  const openMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
  };

  // Guard: si el usuario no está cargado aún, mostrar pantalla de carga
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const myOrders = orders.filter(o => o.delivery_person_id === user.id && o.delivery_status !== 'delivered');
  const availableOrders = orders.filter(o => !o.delivery_person_id && o.status === 'ready');

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-orange-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Panel Delivery</h1>
                <p className="text-xs sm:text-sm text-gray-600">{user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={loadDailySummary}
                variant="outline"
                className="border-orange-200 hover:bg-orange-50 rounded-xl"
              >
                <FileText className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Resumen Diario</span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-orange-200 hover:bg-orange-50 rounded-xl">
                    <User className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="w-4 h-4 mr-2" />
                    Perfil
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Salir
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* My Active Deliveries */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Mis Entregas Activas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {myOrders.map((order) => (
              <Card
                key={order.id}
                data-testid={`my-order-${order.id}`}
                className="p-6 bg-white/80 backdrop-blur-xl border-orange-100 hover:shadow-lg transition-all"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">
                      Orden #{order.order_number}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-500">
                        {new Date(order.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700">
                    {order.delivery_status === 'on_the_way' ? 'En Camino' : order.delivery_status}
                  </Badge>
                </div>

                {/* Address with Maps */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-orange-500" />
                        <p className="text-xs font-semibold text-gray-700">Dirección de Entrega</p>
                      </div>
                      <p className="text-sm text-gray-600 pl-6">{order.delivery_address || 'No especificada'}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openMaps(order.delivery_address)}
                      className="rounded-full border-orange-200"
                      title="Abrir en Google Maps"
                    >
                      <Navigation className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Customer Info */}
                <div className="mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-bold">
                    {order.customer_name?.charAt(0) || 'C'}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-800">{order.customer_name || 'Cliente'}</p>
                    <p className="text-sm text-gray-600">{order.customer_phone || 'Sin teléfono'}</p>
                  </div>
                  {order.customer_phone && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(`tel:${order.customer_phone}`)}
                      className="rounded-full border-orange-200"
                    >
                      <Phone className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Items - Full List */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4 text-gray-600" />
                    <p className="text-xs font-semibold text-gray-700">{order.items.length} Producto(s)</p>
                  </div>
                  <div className="space-y-2 pl-6 max-h-48 overflow-y-auto">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-start text-sm border-b border-gray-100 pb-1 last:border-0">
                        <div className="flex-1">
                          <span className="font-medium text-gray-800">{item.quantity}x</span>
                          <span className="ml-1 text-gray-700">{item.product_name}</span>
                          {item.variant && (
                            <span className="ml-1 text-xs text-gray-500">({item.variant})</span>
                          )}
                          {item.notes && (
                            <p className="text-xs text-orange-600 italic mt-0.5">📝 {item.notes}</p>
                          )}
                        </div>
                        <span className="text-gray-600 font-medium">${formatAmount(item.subtotal)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total */}
                <div className="mb-4 p-3 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-orange-600" />
                      <p className="text-sm font-medium text-gray-700">Total a Cobrar</p>
                    </div>
                    <p className="text-2xl font-bold text-orange-600">${formatAmount(order.total)}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  {order.delivery_status === 'on_the_way' && (
                    <Button
                      onClick={() => completeDelivery(order.id)}
                      disabled={loading}
                      data-testid={`complete-delivery-${order.id}`}
                      className="w-full bg-green-500 hover:bg-green-600 text-white rounded-xl"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Marcar como Entregado
                    </Button>
                  )}
                  <Button
                    onClick={() => copyOrderLink(order.id)}
                    variant="outline"
                    size="sm"
                    className="w-full border-gray-200 rounded-xl"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar Enlace de Tracking
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {myOrders.length === 0 && (
            <Card className="p-8 bg-white/80 backdrop-blur-xl border-orange-100 text-center">
              <Truck className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No tienes entregas activas</p>
            </Card>
          )}
        </div>

        {/* Available Orders */}
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Órdenes Disponibles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableOrders.map((order) => (
              <Card
                key={order.id}
                data-testid={`available-order-${order.id}`}
                className="p-6 bg-white/80 backdrop-blur-xl border-green-100 hover:shadow-lg transition-all"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">
                      Orden #{order.order_number}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-xs text-gray-500">
                        {new Date(order.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-700">
                    ¡Lista!
                  </Badge>
                </div>

                {/* Address with Maps */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-orange-500" />
                        <p className="text-xs font-semibold text-gray-700">Dirección de Entrega</p>
                      </div>
                      <p className="text-sm text-gray-600 pl-6">{order.delivery_address || 'No especificada'}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openMaps(order.delivery_address)}
                      className="rounded-full border-orange-200"
                      title="Ver en Maps"
                    >
                      <Navigation className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Items - Full List */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4 text-gray-600" />
                    <p className="text-xs font-semibold text-gray-700">{order.items.length} Producto(s)</p>
                  </div>
                  <div className="space-y-2 pl-6 max-h-48 overflow-y-auto">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-start text-sm border-b border-gray-100 pb-1 last:border-0">
                        <div className="flex-1">
                          <span className="font-medium text-gray-800">{item.quantity}x</span>
                          <span className="ml-1 text-gray-700">{item.product_name}</span>
                          {item.variant && (
                            <span className="ml-1 text-xs text-gray-500">({item.variant})</span>
                          )}
                          {item.notes && (
                            <p className="text-xs text-orange-600 italic mt-0.5">📝 {item.notes}</p>
                          )}
                        </div>
                        <span className="text-gray-600 font-medium">${formatAmount(item.subtotal)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total */}
                <div className="mb-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      <p className="text-sm font-medium text-gray-700">Total</p>
                    </div>
                    <p className="text-2xl font-bold text-green-600">${formatAmount(order.total)}</p>
                  </div>
                </div>

                {/* Action */}
                <Button
                  onClick={() => acceptOrder(order.id)}
                  disabled={loading}
                  data-testid={`accept-order-${order.id}`}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl shadow-md"
                >
                  <Truck className="w-4 h-4 mr-2" />
                  Aceptar y Comenzar Entrega
                </Button>
              </Card>
            ))}
          </div>

          {availableOrders.length === 0 && (
            <Card className="p-8 bg-white/80 backdrop-blur-xl border-orange-100 text-center">
              <p className="text-gray-500">No hay órdenes disponibles</p>
            </Card>
          )}
        </div>
      </main>

      {/* Dialog: Resumen Diario */}
      <Dialog open={showSummary} onOpenChange={setShowSummary}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">Resumen Diario de Entregas</DialogTitle>
          </DialogHeader>
          {dailySummary && typeof dailySummary === 'object' && dailySummary.date && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4 bg-blue-50">
                  <p className="text-sm text-gray-600 mb-1">Fecha</p>
                  <p className="text-lg font-bold text-gray-800">
                    {new Date(dailySummary.date).toLocaleDateString('es-ES', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </Card>
                <Card className="p-4 bg-green-50">
                  <p className="text-sm text-gray-600 mb-1">Total Entregas</p>
                  <p className="text-2xl font-bold text-green-600">{dailySummary.total_orders}</p>
                </Card>
                <Card className="p-4 bg-orange-50">
                  <p className="text-sm text-gray-600 mb-1">Total Recaudado</p>
                  <p className="text-2xl font-bold text-orange-600">${formatAmount(dailySummary.total_amount)}</p>
                </Card>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3">Detalle de Órdenes</h3>
                {dailySummary.orders && Array.isArray(dailySummary.orders) && dailySummary.orders.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {dailySummary.orders.map((order, index) => (
                      <Card key={order.id} className="p-3 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="bg-blue-50">
                                Orden #{order.order_number || index + 1}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {new Date(order.created_at).toLocaleTimeString('es-ES', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 font-medium">{order.customer_name}</p>
                            <p className="text-xs text-gray-500">{order.customer_address}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-gray-800">${formatAmount(order.total)}</p>
                            <Badge className="mt-1 bg-green-100 text-green-800">
                              {order.payment_method === 'cash' ? 'Efectivo' : 
                               order.payment_method === 'card' ? 'Tarjeta' : 'Transferencia'}
                            </Badge>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card className="p-8 bg-gray-50 text-center">
                    <Package className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500">No hay entregas completadas hoy</p>
                  </Card>
                )}
              </div>

              <Button
                onClick={() => setShowSummary(false)}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 rounded-xl"
              >
                Cerrar
              </Button>
            </div>
          )}
          {!dailySummary || typeof dailySummary !== 'object' || !dailySummary.date && (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No se pudo cargar el resumen diario</p>
              <Button
                onClick={() => setShowSummary(false)}
                variant="outline"
                className="mt-4 rounded-xl"
              >
                Cerrar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryDashboard;
